<template>
  <aside class="kds-sidebar">
    <!-- 1. Workspace switcher -->
    <button
      type="button"
      class="kds-sidebar__workspace-switcher"
      role="button"
      aria-haspopup="listbox"
      aria-expanded="false"
      aria-label="Switch workspace"
      @click="$emit('workspace-switch')"
    >
      <span class="kds-sidebar__ws-logo" aria-hidden="true">
        <span class="kds-sidebar__ws-logo-initials">{{ wsLogoInitials }}</span>
      </span>
      <span class="kds-sidebar__ws-text">
        <span
          class="kds-sidebar__ws-name"
          :title="workspaceName.length > 24 ? workspaceName : undefined"
        >{{ workspaceName }}</span>
        <span class="kds-sidebar__ws-eyebrow">{{ workspaceEyebrowLabel }}</span>
      </span>
      <KdsIcon name="chevron-down" :size="16" class="kds-sidebar__ws-chevron" />
    </button>

    <!-- 2. Search field — wrapped in a button to intercept clicks and trigger global search -->
    <div class="kds-sidebar__search-wrap">
      <button
        type="button"
        class="kds-sidebar__search-trigger"
        :aria-label="searchPlaceholder"
        @click="$emit('search-trigger')"
      >
        <KdsInput
          type="search"
          :model-value="''"
          :placeholder="searchPlaceholder"
          :aria-label="searchPlaceholder"
          autocomplete="off"
          class="kds-sidebar__search-input"
        >
          <template #suffix>
            <kbd class="kds-sidebar__search-shortcut">{{ searchShortcutLabel }}</kbd>
          </template>
        </KdsInput>
      </button>
    </div>

    <!-- 4. WORKSPACE section -->
    <nav
      class="kds-sidebar__workspace-nav"
      role="navigation"
      aria-label="Workspace"
    >
      <span class="kds-sidebar__section-label">{{ sectionWorkspaceLabel }}</span>
      <ul class="kds-sidebar__nav-list" role="menu">
        <li
          v-for="item in navItems"
          :key="item.id"
          role="none"
        >
          <button
            type="button"
            class="kds-sidebar__nav-item"
            :class="{ 'is-active': item.id === activeNavItem }"
            role="menuitem"
            :aria-current="item.id === activeNavItem ? 'page' : undefined"
            @click="$emit('nav-item-click', item.id)"
          >
            <KdsIcon :name="(item.icon as KdsIconName)" :size="20" class="kds-sidebar__nav-icon" />
            <span class="kds-sidebar__nav-label">{{ item.label }}</span>
            <span
              v-if="item.count !== undefined"
              class="kds-sidebar__nav-count"
            >{{ item.count }}</span>
          </button>
        </li>
      </ul>
    </nav>

    <!-- 5. PANELS section -->
    <div class="kds-sidebar__panels-section">
      <!-- Section header: static label, or collapsible toggle button (D2) -->
      <template v-if="panelsCollapsible">
        <button
          type="button"
          class="kds-sidebar__section-toggle"
          :aria-expanded="panelsExpanded ? 'true' : 'false'"
          :aria-controls="panelsSectionId"
          @click="togglePanels"
        >
          <span class="kds-sidebar__section-label kds-sidebar__section-label--toggle">{{ sectionPanelsLabel }}</span>
          <KdsIcon
            name="chevron-down"
            :size="14"
            class="kds-sidebar__toggle-chevron"
            :class="{ 'kds-sidebar__toggle-chevron--collapsed': !panelsExpanded }"
          />
        </button>
      </template>
      <template v-else>
        <span class="kds-sidebar__section-label">{{ sectionPanelsLabel }}</span>
      </template>

      <!-- sr-only live region for panels count -->
      <span
        class="sr-only"
        aria-live="polite"
        aria-atomic="true"
      >{{ panelsCountAnnouncement }}</span>

      <!-- Panels body: hidden when collapsed (D2) -->
      <Transition name="panels-collapse">
        <div
          v-show="!panelsCollapsible || panelsExpanded"
          :id="panelsSectionId"
          class="kds-sidebar__panels-scroll"
        >
          <!-- D7: Loading skeletons when panelsLoading is true -->
          <template v-if="panelsLoading">
            <div class="kds-sidebar__skeletons" aria-hidden="true">
              <div class="kds-sidebar__skeleton" />
              <div class="kds-sidebar__skeleton" />
            </div>
          </template>

          <template v-else>
            <!-- Error slot (takes priority over native error rendering) -->
            <slot name="error">
              <!-- Native error rendering when errorLabel prop is provided -->
              <template v-if="errorLabel">
                <div class="kds-sidebar__error">
                  <KdsIcon name="triangle-alert" :size="18" class="kds-sidebar__error-icon" />
                  <span class="kds-sidebar__error-label">{{ errorLabel }}</span>
                  <button
                    type="button"
                    class="kds-sidebar__error-retry"
                    @click="$emit('retry')"
                  >{{ retryLabel }}</button>
                </div>
              </template>
            </slot>

            <!-- Panels list (only shown when no error) -->
            <template v-if="!errorLabel && !$slots.error">
              <TransitionGroup
                v-if="panels.length > 0"
                name="panel-row"
                tag="ul"
                class="kds-sidebar__panels-list"
                :style="{ '--panel-count': Math.min(panels.length, 10) }"
              >
                <li
                  v-for="(panel, i) in panels"
                  :key="panel.id"
                  :style="{ '--i': i }"
                >
                  <KdsSidebarPanelRow
                    :id="panel.id"
                    :name="panel.name"
                    :protocol="panel.protocol"
                    :persona-count="panel.personaCount"
                    :is-active="panel.id === activePanel"
                    :is-live="panel.isLive"
                    @click="$emit('panel-click', panel.id)"
                  />
                </li>
              </TransitionGroup>

              <!-- Empty state: no panels at all -->
              <Transition name="fade">
                <div
                  v-if="panels.length === 0 && !searchValue"
                  class="kds-sidebar__empty"
                >
                  <slot name="empty">{{ emptyPanelsLabel }}</slot>
                </div>
              </Transition>
            </template>
          </template>
        </div>
      </Transition>
    </div>

    <!-- 6. Profile card -->
    <KdsSmallProfileCard
      :name="member.name"
      :role="member.role"
      :initials="member.initials"
      @settings-click="$emit('settings-click')"
    />
  </aside>
</template>

<script setup lang="ts">
import KdsIcon from '@/kds/primitives/KdsIcon.vue';
import KdsInput from '@/kds/primitives/KdsInput.vue';
import KdsSmallProfileCard from './KdsSmallProfileCard.vue';
import KdsSidebarPanelRow from './KdsSidebarPanelRow.vue';
import type { NavItem, PanelRow, MemberProfile, KdsIconName } from '@/kds/types';
import { computed, ref } from 'vue';

// Stable localStorage key for the collapsible panels section state (D2)
const PANELS_COLLAPSED_KEY = 'kds.sidebar.panels.collapsed';

interface Props {
  workspaceName: string;
  workspaceLogo?: string;
  navItems: NavItem[];
  panels: PanelRow[];
  activeNavItem?: string;
  activePanel?: string;
  member: MemberProfile;
  searchPlaceholder?: string;
  searchShortcutLabel?: string;
  emptyPanelsLabel?: string;
  workspaceEyebrowLabel?: string;
  sectionWorkspaceLabel?: string;
  sectionPanelsLabel?: string;
  /** Translated error message to display in the panels section. When provided, the native error state is shown. */
  errorLabel?: string;
  /** Label for the retry button in the native error state. */
  retryLabel?: string;
  /** sr-only announcement text for the panels count live region (e.g. "5 panels shown"). */
  panelsCountAnnouncement?: string;
  /**
   * When true, the panels section renders a collapse/expand toggle button.
   * The open/closed state is persisted in localStorage under `kds.sidebar.panels.collapsed`. (D2)
   */
  panelsCollapsible?: boolean;
  /**
   * When true, renders 2 skeleton rows instead of the panels list content.
   * Used to indicate a loading state for the first fetch. (D7)
   */
  panelsLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  workspaceLogo: undefined,
  activeNavItem: undefined,
  activePanel: undefined,
  searchPlaceholder: 'Search workspace…',
  searchShortcutLabel: '⌘K',
  emptyPanelsLabel: 'No panel yet.',
  workspaceEyebrowLabel: 'workspace',
  sectionWorkspaceLabel: 'WORKSPACE',
  sectionPanelsLabel: 'PANELS',
  errorLabel: undefined,
  retryLabel: 'Retry',
  panelsCountAnnouncement: '',
  panelsCollapsible: false,
  panelsLoading: false
});

const emit = defineEmits<{
  (e: 'workspace-switch'): void;
  (e: 'search-trigger'): void;
  (e: 'nav-item-click', id: string): void;
  (e: 'panel-click', id: string): void;
  (e: 'settings-click'): void;
  (e: 'retry'): void;
  (e: 'toggle-panels', expanded: boolean): void;
}>();

// Stable id for aria-controls linkage
const panelsSectionId = 'kds-sidebar-panels-body';

// Restore collapsed state from localStorage; default is expanded (D2)
const isCollapsedInStorage = (): boolean => {
  try {
    return localStorage.getItem(PANELS_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const panelsExpanded = ref(!isCollapsedInStorage());

const togglePanels = (): void => {
  panelsExpanded.value = !panelsExpanded.value;
  try {
    localStorage.setItem(PANELS_COLLAPSED_KEY, panelsExpanded.value ? 'false' : 'true');
  } catch {
    // localStorage may be unavailable in private browsing — silently ignore
  }
  emit('toggle-panels', panelsExpanded.value);
};

// Logo initials: first letter of each word in workspace name (max 2)
const wsLogoInitials = computed(() => {
  const words = props.workspaceName.trim().split(/\s+/);
  return words
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
});

</script>

<style scoped lang="scss">
.kds-sidebar {
  display: flex;
  flex-direction: column;
  width: 240px;
  min-width: 240px;
  max-width: 240px;
  height: 100vh;
  position: sticky;
  top: 0;
  background: var(--paper);
  border-right: var(--border);
  overflow: hidden;

  // -----------------------------------------------
  // 1. Workspace switcher
  // -----------------------------------------------
  &__workspace-switcher {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-5) var(--space-4);
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    width: 100%;
    flex-shrink: 0;
    border-radius: var(--r-2);
    transition: background-color 120ms var(--ease-out);

    &:hover {
      background: var(--paper-3);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  }

  &__ws-logo {
    width: 32px;
    height: 32px;
    border-radius: var(--r-pill);
    background: var(--ink);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__ws-logo-initials {
    font-family: var(--font-mono);
    font-weight: var(--fw-semibold);
    font-size: 12px;
    color: var(--paper);
    text-transform: uppercase;
  }

  &__ws-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  &__ws-name {
    font-size: var(--fs-body);
    font-weight: var(--fw-semibold);
    color: var(--ink);
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__ws-eyebrow {
    font-family: var(--font-mono);
    font-size: 10.5px;
    color: var(--stone);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  &__ws-chevron {
    color: var(--stone);
    flex-shrink: 0;
  }

  // -----------------------------------------------
  // 2. Search
  // -----------------------------------------------
  &__search-wrap {
    padding: 0 var(--space-4);
    padding-top: var(--space-3);
    flex-shrink: 0;
  }

  &__search-shortcut {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--stone-2);
    background: transparent;
    border: none;
    flex-shrink: 0;
  }

  // -----------------------------------------------
  // 3. Search trigger button
  // -----------------------------------------------
  &__search-trigger {
    display: block;
    width: 100%;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    text-align: left;

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
      border-radius: var(--r-3);
    }
  }

  &__search-input {
    pointer-events: none;
  }

  // -----------------------------------------------
  // 4. WORKSPACE nav section
  // -----------------------------------------------
  &__workspace-nav {
    padding: var(--space-6) var(--space-4) 0;
    flex-shrink: 0;
  }

  &__section-label {
    display: block;
    font-family: var(--font-mono);
    font-size: 10.5px;
    color: var(--stone);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: var(--space-2);
    padding: 0 var(--space-3);
  }

  &__nav-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__nav-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    height: 36px;
    padding: 0 var(--space-3);
    background: transparent;
    border: none;
    border-radius: var(--r-2);
    cursor: pointer;
    text-align: left;
    color: var(--ink);
    font-size: var(--fs-body);
    font-family: var(--font-ui);
    transition: background-color 150ms var(--ease-out);

    &:hover:not(.is-active) {
      background: var(--paper-3);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }

    &.is-active {
      background: var(--primary-tint);
      margin-left: calc(var(--space-4) * -1);
      padding-left: calc(var(--space-4) - 2px);
      border-left: 2px solid var(--ink);
      border-radius: 0 var(--r-2) var(--r-2) 0;
      width: calc(100% + var(--space-4));

      .kds-sidebar__nav-label {
        font-weight: var(--fw-semibold);
      }
    }
  }

  &__nav-icon {
    color: var(--stone);
    flex-shrink: 0;
  }

  &__nav-label {
    flex: 1;
    color: var(--ink);
  }

  &__nav-count {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    color: var(--stone-2);
    flex-shrink: 0;
  }

  // -----------------------------------------------
  // 5. PANELS section
  // -----------------------------------------------
  &__panels-section {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding-top: var(--space-6);
    overflow: hidden;
  }

  &__panels-section > .kds-sidebar__section-label {
    padding: 0 var(--space-4);
    flex-shrink: 0;
  }

  // Toggle button for collapsible mode (D2)
  &__section-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0 var(--space-4);
    flex-shrink: 0;

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
      border-radius: var(--r-2);
    }
  }

  &__section-label--toggle {
    display: inline;
    padding: 0;
  }

  &__toggle-chevron {
    color: var(--stone);
    flex-shrink: 0;
    transition: transform 200ms var(--ease-out);

    &--collapsed {
      transform: rotate(-90deg);
    }
  }

  // Loading skeletons (D7)
  &__skeletons {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-4);
  }

  &__skeleton {
    height: 28px;
    border-radius: var(--r-2);
    background: linear-gradient(
      90deg,
      var(--paper-3) 0%,
      var(--paper-2) 50%,
      var(--paper-3) 100%
    );
    background-size: 200% 100%;
    animation: kds-skeleton-shimmer 1.4s ease-in-out infinite;
  }

  &__panels-scroll {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
    padding: var(--space-2) 0;

    // Custom thin white rounded scrollbar (vertical + horizontal)
    scrollbar-width: thin;
    scrollbar-color: var(--paper) transparent;

    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--paper);
      border: 1px solid var(--mist);
      border-radius: var(--r-pill);
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: var(--paper);
      border-color: var(--mist-2);
    }

    &::-webkit-scrollbar-corner {
      background: transparent;
    }
  }

  &__panels-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__empty {
    padding: var(--space-4) var(--space-4);
    font-size: var(--fs-body-sm);
    color: var(--stone);
    text-align: left;
  }

  // -----------------------------------------------
  // Native error state (D1)
  // -----------------------------------------------
  &__error {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
    padding: var(--space-4);
  }

  &__error-icon {
    color: var(--signal);
    flex-shrink: 0;
  }

  &__error-label {
    font-size: var(--fs-body-sm);
    color: var(--stone);
  }

  &__error-retry {
    background: transparent;
    border: 1px solid var(--mist-2);
    border-radius: var(--r-2);
    padding: var(--space-1) var(--space-3);
    font-size: var(--fs-body-sm);
    font-family: var(--font-ui);
    color: var(--ink);
    cursor: pointer;
    transition: background-color 120ms var(--ease-out);

    &:hover {
      background: var(--paper-3);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  }
}

// sr-only utility for live region (D2)
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

// TransitionGroup stagger animation for panel rows
.panel-row-enter-active {
  transition:
    opacity 150ms var(--ease-out),
    transform 150ms var(--ease-out);
  transition-delay: calc(var(--i, 0) * 40ms);
}
.panel-row-enter-from {
  opacity: 0;
  transform: translateY(4px);
}
.panel-row-leave-active {
  transition: opacity 100ms var(--ease-out);
}
.panel-row-leave-to {
  opacity: 0;
}

// Fade transition for empty states
.fade-enter-active {
  transition: opacity 200ms var(--ease-out);
}
.fade-enter-from {
  opacity: 0;
}
.fade-leave-active {
  transition: opacity 150ms var(--ease-out);
}
.fade-leave-to {
  opacity: 0;
}

// Collapse/expand transition for the panels section body (D2)
.panels-collapse-enter-active {
  transition: opacity 200ms var(--ease-out);
  overflow: hidden;
}
.panels-collapse-enter-from {
  opacity: 0;
}
.panels-collapse-leave-active {
  transition: opacity 150ms var(--ease-out);
  overflow: hidden;
}
.panels-collapse-leave-to {
  opacity: 0;
}

// Skeleton shimmer keyframe (D7)
@keyframes kds-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .kds-sidebar__skeleton {
    animation: none;
  }
}
</style>
